'use client'

import * as React from 'react'
import { ChevronDown, X } from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import {
  Command,
  CommandGroup,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import { Command as CommandPrimitive } from 'cmdk'
import { useQuery } from '@tanstack/react-query'
import { getFilterCriteriaCountiesGetFilterCriteriaCountiesPostOptions } from '@/generated/@tanstack/react-query.gen'
import { z } from 'zod'
import { toast } from '../custom-toast'

const resonseSchema = z.object({
  status: z.string(),
  county_names: z.array(z.string()),
})

interface Props {
  onRemoveCounty: (county: string) => void
  selected: string[]
  setSelected: (value: string[]) => void
}

export function QueryFilterCounties({
  selected,
  setSelected,
  onRemoveCounty,
}: Props) {
  const inputRef = React.useRef<HTMLInputElement>(null)
  // const scrollContainerRef = React.useRef<HTMLDivElement>(null)
  const [open, setOpen] = React.useState(false)
  const [inputValue, setInputValue] = React.useState('')

  const { data = [], isLoading } = useQuery({
    ...getFilterCriteriaCountiesGetFilterCriteriaCountiesPostOptions({
      body: {},
    }),
    staleTime: 1000 * 60 * 5,
    select: (data) => {
      return resonseSchema.parse(data).county_names
    },
  })

  const handleUnselect = (value: string) => {
    const newSelectedArray = selected.filter((s) => s !== value)
    onRemoveCounty(value)
    setSelected(newSelectedArray)
  }

  const handleKeyDown = React.useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      const input = inputRef.current
      if (input) {
        if (e.key === 'Escape') {
          input.blur()
          setInputValue('')
          setOpen(false)
        }
      }
    },
    []
  )

  const handleSelect = (value: string) => {
    if (selected.includes(value)) {
      toast.warning({
        title: 'Your select has already been selected.',
      })
      return
    }

    setSelected([...selected, value])
    setInputValue('')
    inputRef?.current?.blur()
    setOpen(false)

    // Open when move Ux to scroll right
    // setTimeout(() => {
    //   const container = scrollContainerRef.current
    //   if (container) {
    //     const lastChild = container.lastElementChild
    //     lastChild?.scrollIntoView({
    //       behavior: "smooth",
    //       block: "nearest",
    //       inline: "end",
    //     })
    //   }
    // }, 100)
  }

  // Filter out already selected counties from the data list
  const filteredCounties = data.filter((county) => !selected.includes(county))

  // check filteredCounties contains inputValue
  const isInputValueInFilteredCounties = filteredCounties.some((county) =>
    county.toLowerCase().includes(inputValue.toLowerCase())
  )

  const hasFilteredData =
    filteredCounties.length > 0 && isInputValueInFilteredCounties

  return (
    <Command
      onKeyDown={handleKeyDown}
      className="overflow-visible bg-transparent"
    >
      <div className="text-sm">
        <div className="flex flex-col gap-1">
          <div className="relative">
            <CommandPrimitive.Input
              ref={inputRef}
              value={inputValue}
              onValueChange={setInputValue}
              onBlur={() => setOpen(false)}
              onFocus={() => setOpen(true)}
              placeholder={'Select a County'}
              className="flex-1 bg-transparent outline-none placeholder:text-muted-foreground rounded-lg w-full
              focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary
              "
            />
            <div className="absolute right-2 top-1/2 -translate-y-1/2">
              {isLoading ? (
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
              ) : hasFilteredData ? (
                <ChevronDown
                  className="h-4 w-4 opacity-50 cursor-pointer"
                  onClick={() => inputRef.current?.focus()}
                />
              ) : (
                <></>
              )}
            </div>
          </div>

          <div className="flex flex-wrap gap-1">
            {selected.map((value) => {
              return (
                <Badge className="pl-1 pr-0 py-0" key={value} variant="default">
                  <p className="text-xs line-clamp-2 truncate">{value}</p>
                  <button
                    className="rounded-full outline-none ring-offset-background focus:ring-2 focus:ring-ring focus:ring-offset-2"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleUnselect(value)
                      }
                    }}
                    onMouseDown={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                    }}
                    onClick={() => handleUnselect(value)}
                  >
                    <X className="h-3 w-3 text-white hover:text-foreground" />
                  </button>
                </Badge>
              )
            })}
          </div>
        </div>
      </div>

      {open && hasFilteredData ? (
        <div className="relative mt-0">
          <CommandList>
            <div className="absolute top-0 z-10 w-full rounded-md border bg-popover text-popover-foreground shadow-md outline-none animate-in">
              <CommandGroup className="h-full overflow-auto max-h-[200px]">
                {filteredCounties.map((value, index) => {
                  return (
                    <CommandItem
                      value={value}
                      key={`${value}-${index}`}
                      onMouseDown={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                      }}
                      onSelect={(_) => {
                        handleSelect(value)
                      }}
                      className={'cursor-pointer py-2'}
                    >
                      {value}
                    </CommandItem>
                  )
                })}
              </CommandGroup>
            </div>
          </CommandList>
        </div>
      ) : null}
    </Command>
  )
}
